import i18n, { DEFAULT_RAM_LANGUAGE, RAM_LANGUAGE_STORAGE_KEY, SUPPORTED_RAM_LANGUAGES, type RamLanguage } from '@/shared/i18n/instance';

/** Re-export for UI that lists supported languages without importing the i18n instance module directly. */
export { SUPPORTED_RAM_LANGUAGES };
export type { RamLanguage };

let isGermanLanguageEnabled = false;

export function isGermanUiLanguageEnabled(): boolean {
    return isGermanLanguageEnabled;
}

function readStoredLanguage(): RamLanguage {
    try {
        const persistedValue = localStorage.getItem(RAM_LANGUAGE_STORAGE_KEY);
        return normalizeRamLanguage(persistedValue ?? undefined) ?? DEFAULT_RAM_LANGUAGE;
    } catch {
        return DEFAULT_RAM_LANGUAGE;
    }
}

export function normalizeRamLanguage(value: string | undefined): RamLanguage | undefined {
    const normalizedValue = value?.split('-')[0]?.toLowerCase();
    return SUPPORTED_RAM_LANGUAGES.includes(normalizedValue as RamLanguage) ? (normalizedValue as RamLanguage) : undefined;
}

export function getPersistedLanguage(): RamLanguage {
    if (!isGermanLanguageEnabled) {
        return DEFAULT_RAM_LANGUAGE;
    }
    return readStoredLanguage();
}

/** Apply `F_CHANGE_LANGUAGE_ENABLED` after `/config` loads. Disabled keeps English without wiping a stored DE preference. */
export function syncRamUiLanguage(germanEnabled: boolean): void {
    isGermanLanguageEnabled = germanEnabled;
    const nextLanguage = getPersistedLanguage();
    if (i18n.language === nextLanguage) {
        return;
    }
    try {
        void i18n.changeLanguage(nextLanguage);
    } catch {
        // Best-effort language switching only.
    }
}

/** AppHeader language: explicit → i18n → persisted (same order as investment-case Univer numfmt). */
export function resolveRamLanguage(language?: string): RamLanguage {
    if (!isGermanLanguageEnabled) {
        return DEFAULT_RAM_LANGUAGE;
    }
    return normalizeRamLanguage(language) ?? normalizeRamLanguage(i18n.resolvedLanguage) ?? normalizeRamLanguage(i18n.language) ?? getPersistedLanguage();
}

export function setPersistedLanguage(language: RamLanguage): void {
    if (!isGermanLanguageEnabled && language !== DEFAULT_RAM_LANGUAGE) {
        return;
    }

    try {
        localStorage.setItem(RAM_LANGUAGE_STORAGE_KEY, language);
    } catch {
        // Best-effort persistence only.
    }

    try {
        void i18n.changeLanguage(language);
    } catch {
        // Best-effort language switching only.
    }
}
